import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase/firestore';
import { collection, setDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, getDoc } from "firebase/firestore"; 
import StatsGrid from './StatsGrid';
import LessonModal from './LessonModal';
import TrainingModal from './TrainingModaladmin';
import AudioLessonsModal from './AudioLessonsModal';
import { 
  Plus, Edit, Trash2, Palette, BookOpen, Image, Video,
  Clock, Users, Star, ListChecks, ChevronDown, Crown, File,
  FileText, Music, FileArchive, Download, Eye, Headphones, Droplets,
  ArrowUp, ArrowDown, GripVertical
} from 'lucide-react';

// ============ ВЫНЕСЕННЫЕ КОМПОНЕНТЫ ============

// Компонент карточки урока
const LessonCard = ({ lesson, onEdit, onDelete, loading }) => {
  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return <FileText className="w-4 h-4" />;
    if (fileType.includes('audio')) return <Music className="w-4 h-4" />;
    if (fileType.includes('image')) return <Image className="w-4 h-4" />;
    if (fileType.includes('zip') || fileType.includes('rar')) return <FileArchive className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const getTechniqueStyles = (technique) => {
    switch(technique) {
      case 'Акрил':
        return 'bg-gradient-to-r from-purple-500/20 to-purple-600/20 text-purple-300 border border-purple-500/30';
      case 'Акварель':
        return 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30';
      case 'Видео':
        return 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-300 border border-indigo-500/30';
      default:
        return 'bg-gradient-to-r from-gray-500/20 to-gray-600/20 text-gray-300 border border-gray-500/30';
    }
  };

  const getTechniqueIcon = (technique) => {
    switch (technique) {
      case 'Акрил':
        return <Palette className="w-3 h-3 mr-1.5 inline" />;
      case 'Акварель':
        return <Droplets className="w-3 h-3 mr-1.5 inline" />;
      case 'Видео':
        return <Video className="w-3 h-3 mr-1.5 inline" />;
      default:
        return null;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-5 border border-gray-700/30 hover:border-gray-600/50 transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-start gap-5">
        <div className="w-full md:w-32 h-48 md:h-32 flex-shrink-0 rounded-xl overflow-hidden">
          <img 
            src={lesson.image || 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'} 
            alt={lesson.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTechniqueStyles(lesson.technique)}`}>
                  {getTechniqueIcon(lesson.technique)}
                  {lesson.technique}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  lesson.premium 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-amber-300 border border-yellow-500/30'
                    : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/30'
                }`}>
                  {lesson.premium ? (
                    <span className="flex items-center">
                      <Crown className="w-3 h-3 mr-1.5 fill-amber-500" />
                      Премиум
                    </span>
                  ) : 'Бесплатный'}
                </span>
                {lesson.trainingId && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30 flex items-center">
                    <BookOpen className="w-3 h-3 mr-1.5" />
                    В тренинге
                  </span>
                )}
                {lesson.files && lesson.files.length > 0 && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-300 border border-indigo-500/30 flex items-center">
                    <File className="w-3 h-3 mr-1.5" />
                    {lesson.files.length} файл(ов)
                  </span>
                )}
              </div>
              <h3 className="font-bold text-xl mb-2 text-white">{lesson.title}</h3>
            </div>
          </div>
          
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">{lesson.description}</p>
          
          {lesson.files && lesson.files.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-400">Материалы:</span>
                <div className="flex flex-wrap gap-1">
                  {lesson.files.slice(0, 4).map((file, index) => {
                    const icon = getFileIcon(file.type);
                    let color = 'text-gray-400 hover:text-gray-300';
                    
                    if (file.type.includes('pdf')) color = 'text-red-400 hover:text-red-300';
                    else if (file.type.includes('audio')) color = 'text-green-400 hover:text-green-300';
                    else if (file.type.includes('image')) color = 'text-blue-400 hover:text-blue-300';
                    else if (file.type.includes('zip') || file.type.includes('rar')) color = 'text-yellow-400 hover:text-yellow-300';
                    
                    return (
                      <a
                        key={index}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors ${color}`}
                        title={`${file.name} (${formatFileSize(file.size)})`}
                      >
                        <div className="flex items-center gap-2">
                          {icon}
                          <span className="text-xs hidden sm:inline">
                            {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                          </span>
                        </div>
                      </a>
                    );
                  })}
                  {lesson.files.length > 4 && (
                    <span className="px-3 py-2 text-xs text-gray-500 bg-gray-800/50 rounded-lg">
                      +{lesson.files.length - 4} еще
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-gray-700/50">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Star className="w-4 h-4 mr-1.5 text-yellow-400 fill-yellow-400" />
                <span className="font-medium">{lesson.rating}</span>
              </div>
              <div className="flex items-center">
                <span className="mr-1.5">📊</span>
                <span>{lesson.level}</span>
              </div>
              <div className="flex items-center">
                <span className="mr-1.5">🏷️</span>
                <span>{lesson.category}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(lesson)}
                className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors border border-blue-500/30 flex items-center gap-2"
                title="Редактировать"
                disabled={loading}
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Редактировать</span>
              </button>
              <button
                onClick={() => onDelete(lesson.id)}
                className="px-4 py-2 bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors border border-red-500/30 flex items-center gap-2"
                title="Удалить"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Удалить</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ОБНОВЛЕНО: Компонент карточки тренинга с поддержкой изменения порядка ============
const TrainingCard = ({ 
  training, 
  expandedTraining, 
  onExpand, 
  onEdit, 
  onDelete, 
  onEditLesson,
  onLoadDetails,
  onMoveUp,
  onMoveDown,
  index,
  totalItems,
  loading 
}) => {
  const [loadingDetails, setLoadingDetails] = useState(false);
  const isExpanded = expandedTraining === training.id;

  const getTechniqueStyles = (technique) => {
    switch(technique) {
      case 'Акрил':
        return 'bg-gradient-to-r from-purple-500/20 to-purple-600/20 text-purple-300 border border-purple-500/30';
      case 'Акварель':
        return 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30';
      case 'Видео':
        return 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-300 border border-indigo-500/30';
      default:
        return 'bg-gradient-to-r from-gray-500/20 to-gray-600/20 text-gray-300 border border-gray-500/30';
    }
  };

  const getTechniqueIcon = (technique) => {
    switch (technique) {
      case 'Акрил':
        return <Palette className="w-3 h-3 mr-1.5 inline" />;
      case 'Акварель':
        return <Droplets className="w-3 h-3 mr-1.5 inline" />;
      case 'Видео':
        return <Video className="w-3 h-3 mr-1.5 inline" />;
      default:
        return null;
    }
  };

  const handleExpand = () => {
    onExpand(training.id);
  };

  React.useEffect(() => {
    if (isExpanded && !training.lessonDetails && (training.lessons?.length > 0 || training.audioLessons?.length > 0)) {
      setLoadingDetails(true);
      onLoadDetails(training.id).finally(() => setLoadingDetails(false));
    }
  }, [isExpanded, training.id, training.lessonDetails, training.lessons, training.audioLessons, onLoadDetails]);

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return <FileText className="w-4 h-4" />;
    if (fileType.includes('audio')) return <Music className="w-4 h-4" />;
    if (fileType.includes('image')) return <Image className="w-4 h-4" />;
    if (fileType.includes('zip') || fileType.includes('rar')) return <FileArchive className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const totalLessonsCount = (training.lessons?.length || 0) + (training.audioLessons?.length || 0);

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl overflow-hidden border border-gray-700/30 hover:border-gray-600/50 transition-all duration-300">
      <div 
        className="p-5 cursor-pointer hover:bg-gray-800/20 transition-colors"
        onClick={handleExpand}
      >
        <div className="flex flex-col md:flex-row md:items-start gap-5">
          <div className="w-full md:w-28 h-40 md:h-28 flex-shrink-0 rounded-xl overflow-hidden">
            <img 
              src={training.image || 'https://images.unsplash.com/photo-1513366208864-87536b8bd7b4?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'} 
              alt={training.title}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-400 bg-gray-800/50 px-2 py-0.5 rounded-full">
                    #{index + 1}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTechniqueStyles(training.technique)}`}>
                    {getTechniqueIcon(training.technique)}
                    {training.technique || 'Без техники'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30">
                    {training.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    training.premium 
                      ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-amber-300 border border-yellow-500/30'
                      : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/30'
                  }`}>
                    {training.premium ? (
                      <span className="flex items-center">
                        <Crown className="w-3 h-3 mr-1.5 fill-amber-500" />
                        Премиум
                      </span>
                    ) : 'Бесплатный'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
                    {totalLessonsCount} уроков
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <h3 className="font-bold text-xl mb-2 text-white">{training.title}</h3>
                  <ChevronDown className={`w-5 h-5 text-gray-400 mt-1 ml-2 transition-transform flex-shrink-0 ${
                    isExpanded ? 'rotate-180' : ''
                  }`} />
                </div>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm mb-4 line-clamp-1">{training.description}</p>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1.5" />
                <span>{training.duration}</span>
              </div>
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1.5" />
                <span>{training.instructor || 'Не указан'}</span>
              </div>
              <div className="flex items-center">
                <span className="mr-1.5">📊</span>
                <span>{training.level}</span>
              </div>
              <div className="md:ml-auto flex items-center gap-2">
                {/* Кнопки изменения порядка */}
                <div className="flex items-center gap-1 mr-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp(index);
                    }}
                    disabled={index === 0 || loading}
                    className={`p-2 rounded-lg transition-colors ${
                      index === 0 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                    }`}
                    title="Переместить вверх"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown(index);
                    }}
                    disabled={index === totalItems - 1 || loading}
                    className={`p-2 rounded-lg transition-colors ${
                      index === totalItems - 1 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                    }`}
                    title="Переместить вниз"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <GripVertical className="w-4 h-4 text-gray-600 ml-1" />
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(training);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors border border-blue-500/30 flex items-center gap-2"
                  title="Редактировать"
                  disabled={loading}
                >
                  <Edit className="w-4 h-4" />
                  <span className="hidden sm:inline">Редактировать</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(training.id);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors border border-red-500/30 flex items-center gap-2"
                  title="Удалить"
                  disabled={loading}
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Удалить</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-700/50 p-5 bg-gray-900/20">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-white flex items-center">
              <ListChecks className="w-5 h-5 mr-2" />
              Уроки в тренинге ({totalLessonsCount})
            </h4>
            <span className="text-sm text-gray-400">
              {totalLessonsCount} уроков
            </span>
          </div>
          
          <div className="space-y-3">
            {loadingDetails ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
            ) : training.lessonDetails && training.lessonDetails.length > 0 ? (
              training.lessonDetails.map((lesson, index) => (
                <div 
                  key={lesson.id} 
                  className="flex items-center gap-4 p-4 bg-gray-800/30 rounded-xl hover:bg-gray-800/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={lesson.image || 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'} 
                      alt={lesson.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                      <span className="text-sm font-medium text-white truncate">
                        <span className="text-gray-500 mr-2">#{index + 1}</span>
                        {lesson.type === 'audio' ? (
                          <Headphones className="w-3 h-3 inline mr-1 text-green-400" />
                        ) : (
                          <Video className="w-3 h-3 inline mr-1 text-blue-400" />
                        )}
                        {lesson.title}
                      </span>
                      <div className="flex items-center gap-3 mt-1 sm:mt-0">
                        {lesson.type === 'audio' && (
                          <span className="text-xs text-green-400 flex items-center">
                            <Music className="w-3 h-3 mr-1" />
                            Аудио
                          </span>
                        )}
                        {lesson.premium && (
                          <span className="text-xs text-amber-400 flex items-center">
                            <Crown className="w-3 h-3 mr-1 fill-amber-400" />
                            Премиум
                          </span>
                        )}
                        {lesson.files && lesson.files.length > 0 && (
                          <span className="text-xs text-indigo-400 flex items-center">
                            <File className="w-3 h-3 mr-1" />
                            {lesson.files.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditLesson(lesson);
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300"
                        disabled={loading}
                      >
                        Редактировать
                      </button>
                      <span className="text-xs text-gray-500">•</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(lesson.type === 'audio' ? `/audio/${lesson.id}` : `/lesson/${lesson.id}`, '_blank');
                        }}
                        className="text-xs text-green-400 hover:text-green-300 flex items-center"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Просмотр
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>В этом тренинге пока нет уроков</p>
                <p className="text-sm mt-1">Добавьте уроки в настройках тренинга</p>
              </div>
            )}
          </div>
          
          {training.files && training.files.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-700/50">
              <h5 className="font-medium text-white mb-3 flex items-center">
                <File className="w-4 h-4 mr-2" />
                Файлы тренинга ({training.files.length})
              </h5>
              <div className="flex flex-wrap gap-2">
                {training.files.map((file, index) => (
                  <a
                    key={index}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-sm text-gray-300 hover:text-white transition-colors flex items-center gap-2"
                    title={file.name}
                  >
                    {getFileIcon(file.type)}
                    <span className="truncate max-w-xs">
                      {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Компонент карточки аудио урока
const AudioLessonCard = ({ audioLesson, onEdit, onDelete, loading }) => {
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-5 border border-gray-700/30 hover:border-gray-600/50 transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-start gap-5">
        <div className="w-full md:w-32 h-48 md:h-32 flex-shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center">
          <Headphones className="w-12 h-12 text-emerald-400" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-300 border border-emerald-500/30">
                  Аудио урок
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30">
                  {audioLesson.category}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  audioLesson.premium 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-amber-300 border border-yellow-500/30'
                    : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/30'
                }`}>
                  {audioLesson.premium ? (
                    <span className="flex items-center">
                      <Crown className="w-3 h-3 mr-1.5 fill-amber-500" />
                      Премиум
                    </span>
                  ) : 'Бесплатный'}
                </span>
                {audioLesson.audioFiles && audioLesson.audioFiles.length > 0 && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-300 border border-indigo-500/30 flex items-center">
                    <Music className="w-3 h-3 mr-1.5" />
                    {audioLesson.audioFiles.length} аудио
                  </span>
                )}
              </div>
              <h3 className="font-bold text-xl mb-2 text-white">{audioLesson.title}</h3>
            </div>
          </div>
          
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">{audioLesson.description}</p>
          
          {audioLesson.audioFiles && audioLesson.audioFiles.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-400">Аудио файлы:</span>
                <div className="flex flex-wrap gap-1">
                  {audioLesson.audioFiles.slice(0, 4).map((audio, index) => (
                    <a
                      key={index}
                      href={audio.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-green-400 hover:text-green-300"
                      title={`${audio.name} (${formatFileSize(audio.size)})`}
                    >
                      <div className="flex items-center gap-2">
                        <Music className="w-4 h-4" />
                        <span className="text-xs hidden sm:inline">
                          {audio.name.length > 15 ? audio.name.substring(0, 15) + '...' : audio.name}
                        </span>
                      </div>
                    </a>
                  ))}
                  {audioLesson.audioFiles.length > 4 && (
                    <span className="px-3 py-2 text-xs text-gray-500 bg-gray-800/50 rounded-lg">
                      +{audioLesson.audioFiles.length - 4} еще
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-gray-700/50">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Headphones className="w-4 h-4 mr-1.5 text-emerald-400" />
                <span className="font-medium">{audioLesson.audioFiles?.length || 0} аудио</span>
              </div>
              <div className="flex items-center">
                <span className="mr-1.5">🏷️</span>
                <span>{audioLesson.category}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(audioLesson)}
                className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors border border-blue-500/30 flex items-center gap-2"
                title="Редактировать"
                disabled={loading}
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Редактировать</span>
              </button>
              <button
                onClick={() => onDelete(audioLesson.id)}
                className="px-4 py-2 bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors border border-red-500/30 flex items-center gap-2"
                title="Удалить"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Удалить</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ОСНОВНОЙ КОМПОНЕНТ ============

const AdminAdverts = () => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [audioLessons, setAudioLessons] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingTraining, setEditingTraining] = useState(null);
  const [editingAudioLesson, setEditingAudioLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('lessons');
  const [expandedTraining, setExpandedTraining] = useState(null);
  
  const [categories] = useState(['Медитация', 'Чакры', 'Гармония', 'Мандала', 'Природа', 'Ноктюрн', 'Флора', 'Стихия']);
  const [levels] = useState(['Начинающий', 'Средний', 'Продвинутый']);
  
  const [techniques] = useState(['Акрил', 'Акварель', 'Видео']);
  
  const [trainingCategories] = useState(['Базовый курс', 'База знаний', 'Медитации и практики', 'Мини-пути', 'Техники', 'Гости академии', 'Обучение']);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: 'Начинающий',
    rating: '4.5',
    image: '',
    videoUrl: '',
    category: 'Медитация',
    technique: 'Акрил',
    premium: false,
    meditationFocus: '',
    materials: [''],
    energy: '',
    trainingId: '',
    order: 0,
    files: []
  });

  const [trainingFormData, setTrainingFormData] = useState({
    title: '',
    description: '',
    image: '',
    instructor: '',
    duration: '',
    level: 'Начинающий',
    category: 'Базовый курс',
    technique: 'Акрил',
    premium: false,
    lessons: [],
    audioLessons: [],
    totalLessons: 0,
    completedLessons: 0,
    files: []
  });

  const [audioFormData, setAudioFormData] = useState({
    title: '',
    description: '',
    image: '',
    category: 'Медитация',
    audioFiles: [],
    premium: false
  });

  // Функции для изменения порядка тренингов
  const moveTrainingUp = useCallback((index) => {
    if (index === 0) return;
    
    setTrainings(prev => {
      const newTrainings = [...prev];
      [newTrainings[index - 1], newTrainings[index]] = [newTrainings[index], newTrainings[index - 1]];
      
      // Сохраняем новый порядок в Firebase сразу после обновления состояния
      setTimeout(() => saveTrainingsOrder(newTrainings), 0);
      
      return newTrainings;
    });
  }, []);

  const moveTrainingDown = useCallback((index) => {
    if (index === trainings.length - 1) return;
    
    setTrainings(prev => {
      const newTrainings = [...prev];
      [newTrainings[index], newTrainings[index + 1]] = [newTrainings[index + 1], newTrainings[index]];
      
      // Сохраняем новый порядок в Firebase сразу после обновления состояния
      setTimeout(() => saveTrainingsOrder(newTrainings), 0);
      
      return newTrainings;
    });
  }, []);

  // Функция для сохранения порядка тренингов
  const saveTrainingsOrder = useCallback(async (trainingsToSave) => {
    setLoading(true);
    try {
      const updatePromises = trainingsToSave.map((training, index) => {
        const trainingRef = doc(db, "trainings", training.id);
        return updateDoc(trainingRef, {
          order: index,
          updatedAt: new Date().toISOString()
        }).catch(error => {
          console.error(`Ошибка при обновлении порядка тренинга ${training.id}:`, error);
        });
      });
      
      await Promise.allSettled(updatePromises);
      console.log("Порядок тренингов успешно сохранен");
    } catch (error) {
      console.error("Error saving trainings order:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Оптимизированная загрузка данных
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lessonsSnapshot, trainingsSnapshot, audioSnapshot] = await Promise.all([
        getDocs(query(collection(db, "masterClasses"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "trainings"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "audioLessons"), orderBy("createdAt", "desc")))
      ]);

      const lessonsData = lessonsSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      setLessons(lessonsData);

      const audioData = audioSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setAudioLessons(audioData);

      const lessonsMap = new Map();
      lessonsData.forEach(lesson => {
        lessonsMap.set(lesson.id, lesson);
      });

      const audioMap = new Map();
      audioData.forEach(audio => {
        audioMap.set(audio.id, audio);
      });

      // Обработка тренингов
      let trainingsData = trainingsSnapshot.docs.map(trainingDoc => {
        const trainingData = { 
          id: trainingDoc.id, 
          ...trainingDoc.data() 
        };
        
        const videoLessons = (trainingData.lessons || [])
          .map(lessonId => lessonsMap.get(lessonId))
          .filter(lesson => lesson !== undefined)
          .map(lesson => ({ ...lesson, type: 'video' }));
        
        const audioLessonsList = (trainingData.audioLessons || [])
          .map(audioId => audioMap.get(audioId))
          .filter(audio => audio !== undefined)
          .map(audio => ({ ...audio, type: 'audio' }));
        
        const allLessons = [...videoLessons, ...audioLessonsList];
        
        trainingData.lessonDetails = allLessons;
        
        return trainingData;
      });

      // Сортируем тренинги на клиенте по полю order (если есть) или по дате создания
      trainingsData = trainingsData.sort((a, b) => {
        // Если есть поле order у обоих, сортируем по нему
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Если только у одного есть order, тот у кого есть order идет первым
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        // Если нет order ни у одного, сортируем по дате создания (новые сверху)
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });

      setTrainings(trainingsData);
      
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Функция для загрузки деталей тренинга (ленивая загрузка)
  const loadTrainingDetails = useCallback(async (trainingId) => {
    try {
      const trainingDoc = await getDoc(doc(db, "trainings", trainingId));
      if (trainingDoc.exists()) {
        const trainingData = trainingDoc.data();
        
        const videoLessons = [];
        if (trainingData.lessons && trainingData.lessons.length > 0) {
          const lessonPromises = trainingData.lessons.map(lessonId => 
            getDoc(doc(db, "masterClasses", lessonId))
          );
          
          const lessonResults = await Promise.all(lessonPromises);
          lessonResults
            .filter(lessonDoc => lessonDoc.exists())
            .forEach(lessonDoc => {
              videoLessons.push({ 
                id: lessonDoc.id, 
                ...lessonDoc.data(),
                type: 'video'
              });
            });
        }

        const audioLessonsList = [];
        if (trainingData.audioLessons && trainingData.audioLessons.length > 0) {
          const audioPromises = trainingData.audioLessons.map(audioId => 
            getDoc(doc(db, "audioLessons", audioId))
          );
          
          const audioResults = await Promise.all(audioPromises);
          audioResults
            .filter(audioDoc => audioDoc.exists())
            .forEach(audioDoc => {
              audioLessonsList.push({ 
                id: audioDoc.id, 
                ...audioDoc.data(),
                type: 'audio'
              });
            });
        }
        
        const allLessons = [...videoLessons, ...audioLessonsList];
        
        setTrainings(prev => prev.map(training => 
          training.id === trainingId 
            ? { ...training, lessonDetails: allLessons } 
            : training
        ));
      }
      return Promise.resolve();
    } catch (error) {
      console.error(`Error loading training details for ${trainingId}:`, error);
      return Promise.reject(error);
    }
  }, []);

  const handleExpandTraining = useCallback((trainingId) => {
    setExpandedTraining(prev => prev === trainingId ? null : trainingId);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTrainingInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTrainingFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTrainingTechniqueChange = (technique) => {
    setTrainingFormData(prev => ({ ...prev, technique }));
  };

  const handleMaterialChange = (index, value) => {
    const newMaterials = [...formData.materials];
    newMaterials[index] = value;
    setFormData(prev => ({ ...prev, materials: newMaterials }));
  };

  const addMaterialField = () => {
    setFormData(prev => ({ ...prev, materials: [...prev.materials, ''] }));
  };

  const removeMaterialField = (index) => {
    if (formData.materials.length > 1) {
      const newMaterials = formData.materials.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, materials: newMaterials }));
    }
  };

  const addAudioLessonToTraining = (audioLessonId) => {
    if (!trainingFormData.audioLessons.includes(audioLessonId)) {
      setTrainingFormData(prev => ({
        ...prev,
        audioLessons: [...prev.audioLessons, audioLessonId],
        totalLessons: (prev.lessons?.length || 0) + (prev.audioLessons?.length || 0) + 1
      }));
    }
  };

  const removeAudioLessonFromTraining = (audioLessonId) => {
    setTrainingFormData(prev => ({
      ...prev,
      audioLessons: prev.audioLessons.filter(id => id !== audioLessonId),
      totalLessons: (prev.lessons?.length || 0) + (prev.audioLessons?.length || 0) - 1
    }));
  };

  const moveAudioLessonInTraining = (index, direction) => {
    const newAudioLessons = [...trainingFormData.audioLessons];
    if (direction === 'up' && index > 0) {
      [newAudioLessons[index], newAudioLessons[index - 1]] = [newAudioLessons[index - 1], newAudioLessons[index]];
    } else if (direction === 'down' && index < newAudioLessons.length - 1) {
      [newAudioLessons[index], newAudioLessons[index + 1]] = [newAudioLessons[index + 1], newAudioLessons[index]];
    }
    setTrainingFormData(prev => ({ ...prev, audioLessons: newAudioLessons }));
  };

  const addLessonToTraining = (lessonId) => {
    if (!trainingFormData.lessons.includes(lessonId)) {
      setTrainingFormData(prev => ({
        ...prev,
        lessons: [...prev.lessons, lessonId],
        totalLessons: (prev.lessons?.length || 0) + (prev.audioLessons?.length || 0) + 1
      }));
    }
  };

  const removeLessonFromTraining = (lessonId) => {
    setTrainingFormData(prev => ({
      ...prev,
      lessons: prev.lessons.filter(id => id !== lessonId),
      totalLessons: (prev.lessons?.length || 0) + (prev.audioLessons?.length || 0) - 1
    }));
  };

  const moveLessonInTraining = (index, direction) => {
    const newLessons = [...trainingFormData.lessons];
    if (direction === 'up' && index > 0) {
      [newLessons[index], newLessons[index - 1]] = [newLessons[index - 1], newLessons[index]];
    } else if (direction === 'down' && index < newLessons.length - 1) {
      [newLessons[index], newLessons[index + 1]] = [newLessons[index + 1], newLessons[index]];
    }
    setTrainingFormData(prev => ({ ...prev, lessons: newLessons }));
  };

  const handleAddNewLesson = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      level: 'Начинающий',
      rating: '4.5',
      image: '',
      videoUrl: '',
      category: 'Медитация',
      technique: 'Акрил',
      premium: false,
      meditationFocus: '',
      materials: [''],
      energy: '',
      trainingId: '',
      order: 0,
      files: []
    });
    setEditingLesson(null);
    setShowModal(true);
  }, []);

const handleAddNewTraining = useCallback(() => {
  setTrainingFormData({
    title: '',
    description: '',
    image: '',
    instructor: '',
    duration: '',
    level: 'Начинающий',
    category: 'Базовый курс',
    technique: 'Акрил',
    premium: false,
    lessons: [],
    audioLessons: [],
    totalLessons: 0,
    completedLessons: 0,
    files: [],
    status: 'draft', // Добавьте эту строку
    createdAt: new Date().toISOString() // Также добавьте createdAt для порядка
  });
  setEditingTraining(null);
  setShowTrainingModal(true);
}, []);

  const handleAddNewAudioLesson = useCallback(() => {
    setAudioFormData({
      title: '',
      description: '',
      image: '',
      category: 'Медитация',
      audioFiles: [],
      premium: false
    });
    setEditingAudioLesson(null);
    setShowAudioModal(true);
  }, []);

  const handleEditLesson = useCallback((lesson) => {
    setFormData({
      title: lesson.title || '',
      description: lesson.description || '',
      level: lesson.level || 'Начинающий',
      rating: lesson.rating?.toString() || '4.5',
      image: lesson.image || '',
      videoUrl: lesson.videoUrl || '',
      category: lesson.category || 'Медитация',
      technique: lesson.technique || 'Акрил',
      premium: lesson.premium || false,
      meditationFocus: lesson.meditationFocus || '',
      materials: lesson.materials?.length > 0 ? lesson.materials : [''],
      energy: lesson.energy || '',
      trainingId: lesson.trainingId || '',
      order: lesson.order || 0,
      files: lesson.files || [],
      createdAt: lesson.createdAt,
      updatedAt: lesson.updatedAt
    });
    setEditingLesson(lesson.id);
    setShowModal(true);
  }, []);

  const handleEditTraining = useCallback((training) => {
    setTrainingFormData({
      title: training.title || '',
      description: training.description || '',
      image: training.image || '',
      instructor: training.instructor || '',
      duration: training.duration || '',
      level: training.level || 'Начинающий',
      category: training.category || 'Базовый курс',
      technique: training.technique || 'Акрил',
      premium: training.premium || false,
      lessons: training.lessons || [],
      audioLessons: training.audioLessons || [],
      totalLessons: training.totalLessons || 0,
      completedLessons: training.completedLessons || 0,
      files: training.files || [],
      createdAt: training.createdAt,
      updatedAt: training.updatedAt
    });
    setEditingTraining(training.id);
    setShowTrainingModal(true);
  }, []);

  const handleEditAudioLesson = useCallback((audioLesson) => {
    setAudioFormData({
      title: audioLesson.title || '',
      description: audioLesson.description || '',
      image: audioLesson.image || '',
      category: audioLesson.category || 'Медитация',
      audioFiles: audioLesson.audioFiles || [],
      premium: audioLesson.premium || false,
      createdAt: audioLesson.createdAt,
      updatedAt: audioLesson.updatedAt
    });
    setEditingAudioLesson(audioLesson.id);
    setShowAudioModal(true);
  }, []);

  const handleSaveLesson = async (lessonData) => {
    const dataToSave = lessonData || formData;
    
    if (!dataToSave.title) {
      alert("Пожалуйста, заполните обязательное поле: название");
      return;
    }

    setLoading(true);
    try {
      const kinescopeId = extractKinescopeId(dataToSave.videoUrl);
      const videoEmbedCode = kinescopeId ? createKinescopeEmbed(kinescopeId) : dataToSave.videoUrl;
      
      const lessonDataToSave = {
        title: dataToSave.title,
        description: dataToSave.description || '',
        level: dataToSave.level,
        rating: parseFloat(dataToSave.rating),
        image: dataToSave.image,
        videoUrl: videoEmbedCode,
        kinescopeId: kinescopeId,
        category: dataToSave.category,
        technique: dataToSave.technique,
        premium: dataToSave.premium,
        meditationFocus: dataToSave.meditationFocus,
        materials: dataToSave.materials.filter(m => m.trim() !== ''),
        energy: dataToSave.energy,
        trainingId: dataToSave.trainingId,
        order: dataToSave.order,
        files: dataToSave.files || [],
        createdAt: editingLesson ? dataToSave.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingLesson) {
        await updateDoc(doc(db, "masterClasses", editingLesson), lessonDataToSave);
      } else {
        const newDocRef = doc(collection(db, "masterClasses"));
        await setDoc(newDocRef, {
          ...lessonDataToSave,
          id: newDocRef.id,
          createdAt: new Date().toISOString()
        });
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      console.error("Error saving lesson:", error);
      alert("Ошибка при сохранении урока");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTraining = async (trainingData) => {
    const dataToSave = trainingData || trainingFormData;
    
    if (!dataToSave.title) {
      alert("Пожалуйста, заполните обязательное поле: название");
      return;
    }

    setLoading(true);
    try {
      const trainingDataToSave = {
        title: dataToSave.title,
        description: dataToSave.description || '',
        image: dataToSave.image || '',
        instructor: dataToSave.instructor || '',
        duration: `${dataToSave.duration || ''}`,
        level: dataToSave.level || 'Начинающий',
        category: dataToSave.category || 'Базовый курс',
        technique: dataToSave.technique || 'Акрил',
        premium: dataToSave.premium || false,
        lessons: dataToSave.lessons || [],
        audioLessons: dataToSave.audioLessons || [],
        totalLessons: (dataToSave.lessons?.length || 0) + (dataToSave.audioLessons?.length || 0),
        completedLessons: dataToSave.completedLessons || 0,
        files: dataToSave.files || [],
        createdAt: dataToSave.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingTraining) {
        await updateDoc(doc(db, "trainings", editingTraining), trainingDataToSave);
        
        const updatePromises = [];
        for (const lessonId of trainingDataToSave.lessons) {
          try {
            const lessonRef = doc(db, "masterClasses", lessonId);
            updatePromises.push(
              updateDoc(lessonRef, {
                trainingId: editingTraining,
                updatedAt: new Date().toISOString()
              }).catch(error => {
                console.error(`Ошибка при обновлении урока ${lessonId}:`, error);
              })
            );
          } catch (error) {
            console.error(`Ошибка при обновлении урока ${lessonId}:`, error);
          }
        }
        
        const oldTrainingDoc = await getDoc(doc(db, "trainings", editingTraining));
        if (oldTrainingDoc.exists()) {
          const oldTrainingData = oldTrainingDoc.data();
          const removedLessons = oldTrainingData.lessons?.filter(id => 
            !trainingDataToSave.lessons.includes(id)
          ) || [];
          
          for (const lessonId of removedLessons) {
            try {
              const lessonRef = doc(db, "masterClasses", lessonId);
              updatePromises.push(
                updateDoc(lessonRef, {
                  trainingId: '',
                  updatedAt: new Date().toISOString()
                }).catch(error => {
                  console.error(`Ошибка при очистке trainingId у урока ${lessonId}:`, error);
                })
              );
            } catch (error) {
              console.error(`Ошибка при очистке trainingId у урока ${lessonId}:`, error);
            }
          }
        }
        
        if (updatePromises.length > 0) {
          await Promise.allSettled(updatePromises);
        }
      } else {
        const newDocRef = doc(collection(db, "trainings"));
        trainingDataToSave.id = newDocRef.id;
        trainingDataToSave.createdAt = new Date().toISOString();
        trainingDataToSave.order = trainings.length; // Устанавливаем порядок при создании
        trainingDataToSave.status = trainingDataToSave.status || 'draft';
        await setDoc(newDocRef, trainingDataToSave);
        
        const updatePromises = [];
        for (const lessonId of trainingDataToSave.lessons) {
          try {
            const lessonRef = doc(db, "masterClasses", lessonId);
            updatePromises.push(
              updateDoc(lessonRef, {
                trainingId: newDocRef.id,
                updatedAt: new Date().toISOString()
              }).catch(error => {
                console.error(`Ошибка при обновлении урока ${lessonId}:`, error);
              })
            );
          } catch (error) {
            console.error(`Ошибка при обновлении урока ${lessonId}:`, error);
          }
        }
        
        if (updatePromises.length > 0) {
          await Promise.allSettled(updatePromises);
        }
      }

      setShowTrainingModal(false);
      loadData();
    } catch (error) {
      console.error("Error saving training:", error);
      alert(`Ошибка при сохранении тренинга: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAudioLesson = async () => {
    if (!audioFormData.title || !audioFormData.description) {
      alert("Пожалуйста, заполните обязательные поля: название и описание");
      return;
    }

    setLoading(true);
    try {
      const audioLessonData = {
        title: audioFormData.title,
        description: audioFormData.description,
        image: audioFormData.image,
        category: audioFormData.category,
        audioFiles: audioFormData.audioFiles || [],
        premium: audioFormData.premium,
        createdAt: editingAudioLesson ? audioFormData.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingAudioLesson) {
        await updateDoc(doc(db, "audioLessons", editingAudioLesson), audioLessonData);
      } else {
        const newDocRef = doc(collection(db, "audioLessons"));
        await setDoc(newDocRef, {
          ...audioLessonData,
          id: newDocRef.id,
          createdAt: new Date().toISOString()
        });
      }

      setShowAudioModal(false);
      loadData();
    } catch (error) {
      console.error("Error saving audio lesson:", error);
      alert("Ошибка при сохранении аудио урока");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот урок?")) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "masterClasses", id));
      loadData();
    } catch (error) {
      console.error("Error deleting lesson:", error);
      alert("Ошибка при удалении урока");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTraining = async (id) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот тренинг?")) return;

    setLoading(true);
    try {
      const trainingDoc = await getDoc(doc(db, "trainings", id));
      if (trainingDoc.exists()) {
        const trainingData = trainingDoc.data();
        
        const updatePromises = [];
        
        if (trainingData.lessons) {
          for (const lessonId of trainingData.lessons) {
            try {
              const lessonRef = doc(db, "masterClasses", lessonId);
              updatePromises.push(
                updateDoc(lessonRef, {
                  trainingId: '',
                  updatedAt: new Date().toISOString()
                }).catch(error => {
                  console.error(`Ошибка при очистке trainingId у урока ${lessonId}:`, error);
                })
              );
            } catch (error) {
              console.error(`Ошибка при очистке trainingId у урока ${lessonId}:`, error);
            }
          }
        }
        
        if (updatePromises.length > 0) {
          await Promise.allSettled(updatePromises);
        }
      }
      
      await deleteDoc(doc(db, "trainings", id));
      loadData();
    } catch (error) {
      console.error("Error deleting training:", error);
      alert(`Ошибка при удалении тренинга: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudioLesson = async (id) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот аудио урок?")) return;

    setLoading(true);
    try {
      const trainingsWithAudio = trainings.filter(t => 
        t.audioLessons && t.audioLessons.includes(id)
      );
      
      if (trainingsWithAudio.length > 0) {
        const confirmDelete = window.confirm(
          `Этот аудиоурок используется в ${trainingsWithAudio.length} тренинге(ах). Удалить его из тренингов?`
        );
        
        if (confirmDelete) {
          for (const training of trainingsWithAudio) {
            const newAudioLessons = (training.audioLessons || []).filter(audioId => audioId !== id);
            await updateDoc(doc(db, "trainings", training.id), {
              audioLessons: newAudioLessons,
              totalLessons: (training.lessons?.length || 0) + newAudioLessons.length,
              updatedAt: new Date().toISOString()
            });
          }
        } else {
          setLoading(false);
          return;
        }
      }
      
      await deleteDoc(doc(db, "audioLessons", id));
      loadData();
    } catch (error) {
      console.error("Error deleting audio lesson:", error);
      alert("Ошибка при удалении аудио урока");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => ({
    totalLessons: lessons.length,
    acryl: lessons.filter(l => l.technique === 'Акрил').length,
    watercolor: lessons.filter(l => l.technique === 'Акварель').length,
    video: lessons.filter(l => l.technique === 'Видео').length,
    freeLessons: lessons.filter(l => !l.premium).length,
    premiumLessons: lessons.filter(l => l.premium).length,
    totalTrainings: trainings.length,
    acrylTrainings: trainings.filter(t => t.technique === 'Акрил').length,
    watercolorTrainings: trainings.filter(t => t.technique === 'Акварель').length,
    videoTrainings: trainings.filter(t => t.technique === 'Видео').length,
    premiumTrainings: trainings.filter(t => t.premium).length,
    trainingsWithLessons: trainings.filter(t => (t.lessons && t.lessons.length > 0) || (t.audioLessons && t.audioLessons.length > 0)).length,
    trainingLessons: trainings.reduce((total, training) => total + (training.lessons?.length || 0) + (training.audioLessons?.length || 0), 0),
    lessonsWithFiles: lessons.filter(l => l.files && l.files.length > 0).length,
    totalFiles: lessons.reduce((total, lesson) => total + (lesson.files?.length || 0), 0),
    totalAudioLessons: audioLessons.length,
    audioWithFiles: audioLessons.filter(al => al.audioFiles && al.audioFiles.length > 0).length,
    totalAudioFiles: audioLessons.reduce((total, audio) => total + (audio.audioFiles?.length || 0), 0),
    freeAudioLessons: audioLessons.filter(al => !al.premium).length,
    premiumAudioLessons: audioLessons.filter(al => al.premium).length
  }), [lessons, trainings, audioLessons]);

  const extractKinescopeId = (embedCode) => {
    if (!embedCode) return '';
    
    const regex = /kinescope\.io\/embed\/([a-zA-Z0-9]+)/;
    const match = embedCode.match(regex);
    
    if (match && match[1]) {
      return match[1];
    }
    
    const idRegex = /^[a-zA-Z0-9]{10,}$/;
    if (idRegex.test(embedCode.trim())) {
      return embedCode.trim();
    }
    
    return '';
  };

  const createKinescopeEmbed = (videoId) => {
    if (!videoId) return '';
    
    return `<div style="position: relative; padding-top: 56.25%; width: 100%">
    <iframe 
      src="https://kinescope.io/embed/${videoId}" 
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write; screen-wake-lock;" 
      frameborder="0" 
      allowfullscreen 
      style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;">
    </iframe>
  </div>`;
  };

  return (
  <div className="fixed inset-0 bg-[#252626] text-white overflow-hidden">
    <div className="h-full overflow-y-auto overscroll-contain p-4 md:p-6">
        <div className="mb-8 pt-16">
          <p className="text-gray-400">Управление мастер-классами, тренингами и аудио уроками</p>
        </div>

        <div className="flex mb-6 bg-gray-800 rounded-full p-1 max-w-2xl mx-auto">
          <button
            className={`flex-1 py-3 px-2 rounded-full text-center font-medium transition-all duration-300 flex items-center justify-center ${
              activeTab === 'lessons'
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg'
                : 'text-gray-300 hover:text-white'
            }`}
            onClick={() => setActiveTab('lessons')}
          >
            <Palette className="w-5 h-5 mr-2" />
            Уроки ({stats.totalLessons})
          </button>
          <button
            className={`flex-1 py-2 px-2 rounded-full text-center font-medium transition-all duration-400 flex items-center justify-center ${
              activeTab === 'trainings'
                ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg'
                : 'text-gray-300 hover:text-white'
            }`}
            onClick={() => setActiveTab('trainings')}
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Составные уроки ({stats.totalTrainings})
          </button>
          <button
            className={`flex-1 py-2 px-2 rounded-full text-center font-medium transition-all duration-300 flex items-center justify-center ${
              activeTab === 'audio'
                ? 'bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-lg'
                : 'text-gray-300 hover:text-white'
            }`}
            onClick={() => setActiveTab('audio')}
          >
            <Headphones className="w-5 h-5 mr-2" />
            Аудио уроки ({stats.totalAudioLessons})
          </button>
        </div>

        <StatsGrid activeTab={activeTab} stats={stats} />

        <div className="flex gap-4 mb-8">
          <button
            onClick={() => {
              if (activeTab === 'lessons') handleAddNewLesson();
              else if (activeTab === 'trainings') handleAddNewTraining();
              else if (activeTab === 'audio') handleAddNewAudioLesson();
            }}
            className={`flex-1 py-4 px-6 rounded-2xl font-bold text-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center shadow-lg ${
              activeTab === 'lessons' 
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 hover:shadow-purple-500/25'
                : activeTab === 'trainings'
                ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:shadow-blue-500/25'
                : 'bg-gradient-to-r from-green-600 to-emerald-500 hover:shadow-green-500/25'
            }`}
            disabled={loading}
          >
            <Plus className="w-6 h-6 mr-3" />
            Добавить {activeTab === 'lessons' ? 'новый урок' : activeTab === 'trainings' ? 'новый тренинг' : 'новый аудио урок'}
          </button>
        </div>

        {activeTab === 'lessons' ? (
          <div className="space-y-4">
            {lessons.length === 0 ? (
              <div className="text-center py-16 text-gray-500 bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-2xl border border-gray-700/30">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <Image className="w-10 h-10 text-gray-600" />
                </div>
                <p className="text-xl mb-2">Нет созданных уроков</p>
                <p className="text-gray-400 mb-6">Начните с добавления первого урока</p>
                <button
                  onClick={handleAddNewLesson}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg font-medium hover:opacity-90 transition-opacity"
                  disabled={loading}
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Создать первый урок
                </button>
              </div>
            ) : (
              lessons.map(lesson => (
                <LessonCard 
                  key={lesson.id} 
                  lesson={lesson} 
                  onEdit={handleEditLesson}
                  onDelete={handleDeleteLesson}
                  loading={loading}
                />
              ))
            )}
          </div>
        ) : activeTab === 'trainings' ? (
          <div className="space-y-4">
            {trainings.length === 0 ? (
              <div className="text-center py-16 text-gray-500 bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-2xl border border-gray-700/30">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-gray-600" />
                </div>
                <p className="text-xl mb-2">Нет созданных тренингов</p>
                <p className="text-gray-400 mb-6">Начните с добавления первого тренинга</p>
                <button
                  onClick={handleAddNewTraining}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg font-medium hover:opacity-90 transition-opacity"
                  disabled={loading}
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Создать первый тренинг
                </button>
              </div>
            ) : (
              trainings.map((training, index) => (
                <TrainingCard 
                  key={training.id} 
                  training={training} 
                  expandedTraining={expandedTraining}
                  onExpand={handleExpandTraining}
                  onEdit={handleEditTraining}
                  onDelete={handleDeleteTraining}
                  onEditLesson={handleEditLesson}
                  onLoadDetails={loadTrainingDetails}
                  onMoveUp={moveTrainingUp}
                  onMoveDown={moveTrainingDown}
                  index={index}
                  totalItems={trainings.length}
                  loading={loading}
                />
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {audioLessons.length === 0 ? (
              <div className="text-center py-16 text-gray-500 bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-2xl border border-gray-700/30">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <Headphones className="w-10 h-10 text-gray-600" />
                </div>
                <p className="text-xl mb-2">Нет созданных аудио уроков</p>
                <p className="text-gray-400 mb-6">Начните с добавления первого аудио урока</p>
                <button
                  onClick={handleAddNewAudioLesson}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-500 rounded-lg font-medium hover:opacity-90 transition-opacity"
                  disabled={loading}
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Создать первый аудио урок
                </button>
              </div>
            ) : (
              audioLessons.map(audioLesson => (
                <AudioLessonCard 
                  key={audioLesson.id} 
                  audioLesson={audioLesson} 
                  onEdit={handleEditAudioLesson}
                  onDelete={handleDeleteAudioLesson}
                  loading={loading}
                />
              ))
            )}
          </div>
        )}

        <LessonModal
          showModal={showModal}
          setShowModal={setShowModal}
          formData={formData}
          setFormData={setFormData}
          handleInputChange={handleInputChange}
          editingLesson={editingLesson}
          loading={loading}
          handleSaveLesson={handleSaveLesson}
          handleMaterialChange={handleMaterialChange}
          addMaterialField={addMaterialField}
          removeMaterialField={removeMaterialField}
          extractKinescopeId={extractKinescopeId}
          createKinescopeEmbed={createKinescopeEmbed}
          categories={categories}
          levels={levels}
          techniques={techniques}
        />

        <TrainingModal
          showTrainingModal={showTrainingModal}
          setShowTrainingModal={setShowTrainingModal}
          trainingFormData={trainingFormData}
          setTrainingFormData={setTrainingFormData}
          handleTrainingInputChange={handleTrainingInputChange}
          handleTrainingTechniqueChange={handleTrainingTechniqueChange}
          addLessonToTraining={addLessonToTraining}
          removeLessonFromTraining={removeLessonFromTraining}
          moveLessonInTraining={moveLessonInTraining}
          addAudioLessonToTraining={addAudioLessonToTraining}
          removeAudioLessonFromTraining={removeAudioLessonFromTraining}
          moveAudioLessonInTraining={moveAudioLessonInTraining}
          editingTraining={editingTraining}
          loading={loading}
          handleSaveTraining={handleSaveTraining}
          allLessons={lessons}
          allAudioLessons={audioLessons}
          techniques={techniques}
          trainingCategories={trainingCategories}
          levels={levels}
        />

        <AudioLessonsModal
          showModal={showAudioModal}
          setShowModal={setShowAudioModal}
          formData={audioFormData}
          setFormData={setAudioFormData}
          editingItem={editingAudioLesson}
          loading={loading}
          handleSave={handleSaveAudioLesson}
          categories={categories}
        />

        <div className="mt-12 pt-8 border-t border-gray-700/30 text-center text-gray-500 text-sm">
          <p>Административная панель • Всего элементов: {stats.totalLessons + stats.totalTrainings + stats.totalAudioLessons}</p>
          <p className="mt-1">
            Уроков с файлами: {stats.lessonsWithFiles} • Всего файлов: {stats.totalFiles} • Аудио уроков: {stats.totalAudioLessons}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAdverts;